import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import {
  addEventToTracking,
  ProductPropertyMap,
  ProductType
} from "@website/tracking/user";
import isEqual from "lodash-es/isEqual";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawSessionStore = Record<string, any>;

const STORAGE_KEY = "funnel_data";
const isClient = typeof window !== "undefined";

function readFromSession(): RawSessionStore {
  if (!isClient) return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeToSession(data: RawSessionStore) {
  if (!isClient) return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function extractFunnelData<K extends ProductType>(
  store: RawSessionStore,
  funnelKey: K
): ProductPropertyMap[K] {
  return (store[funnelKey] || {}) as ProductPropertyMap[K];
}

export const FunnelStorageSDK = {
  getFunnelData<K extends ProductType>(
    funnelKey: K
  ): Partial<ProductPropertyMap[K]> {
    if (!isClient) return {};
    return extractFunnelData(readFromSession(), funnelKey);
  },

  removeFunnel<K extends ProductType>(funnelKey: K) {
    if (!isClient) return {};
    const store = readFromSession();
    if (funnelKey in store) {
      delete store[funnelKey];
      writeToSession(store);
    }
  },

  isFunnelDataAvailable<K extends ProductType>(
    funnelKey: K,
    firstLoadPropertyName?: string
  ): boolean {
    if (!isClient) return false;
    const store = readFromSession();
    const funnelData = store?.[funnelKey];

    if (!funnelData || Object.keys(funnelData).length === 0) {
      return false;
    }

    if (
      firstLoadPropertyName &&
      funnelData[firstLoadPropertyName] === undefined
    ) {
      return false;
    }

    return true;
  },

  removeFunnelFields<K extends ProductType>(
    funnelKey: K,
    fieldKeys: Array<keyof ProductPropertyMap[K]>
  ) {
    if (!isClient) return {};
    const store = readFromSession();
    const funnelData = { ...extractFunnelData(store, funnelKey) };

    for (const key of fieldKeys) {
      delete funnelData[key];
    }

    store[funnelKey] = funnelData;
    writeToSession(store);
  },

  updateFunnelFields<K extends ProductType>(
    funnelKey: K,
    updates: Partial<ProductPropertyMap[K]>
  ) {
    if (!isClient) return {};
    const store = readFromSession();
    const current = extractFunnelData(store, funnelKey);
    const changed: Partial<ProductPropertyMap[K]> = {};

    for (const key in updates) {
      if (!isEqual(updates[key], current[key])) {
        changed[key] = updates[key];
      }
    }

    if (Object.keys(changed).length > 0) {
      store[funnelKey] = {
        ...current,
        ...changed
      };

      writeToSession(store);
    }
  },

  addEventToTrackingFunnel<K extends ProductType>(
    eventName: USER_TRACKING_EVENTS,
    funnelKey: K,
    newAttributes?: Partial<ProductPropertyMap[K]>,
    resetFunnel = false
  ) {
    if (!isClient) return {};
    if (resetFunnel) {
      FunnelStorageSDK.removeFunnel(funnelKey);
    }
    if (newAttributes) {
      FunnelStorageSDK.updateFunnelFields(funnelKey, newAttributes);
    }
    addEventToTracking(eventName, {
      product: funnelKey,
      ...FunnelStorageSDK.getFunnelData(funnelKey)
    });
  }
};
